import logger from 'loglevel';
const log = logger.getLogger('table-client');
log.setDefaultLevel('INFO');
import { compareKey } from './index.js';
import { TableClient } from './tableClient.js';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { first, skip } from 'rxjs/operators';
import randomString from './util/randomString.js';
import { isDeepEqual } from 'remeda';
export class SimpleTableClient extends TableClient {
    // tslint:disable-next-line: variable-name
    _baseKey;
    get baseKey() {
        return this._baseKey;
    }
    set baseKey(key) {
        this._baseKey = key;
        this.bind();
    }
    constructor(client, key, filterText, sort = {}) {
        super(client, key);
        this._baseKey = key;
        this._filter = filterText;
        this._sort = sort;
    }
    // tslint:disable-next-line: variable-name
    _filter;
    backupFilter;
    get filter() {
        return this._filter;
    }
    set filter(filterText) {
        this._filter = filterText;
        this.bind();
    }
    // tslint:disable-next-line: variable-name
    _sort = {};
    backupSort;
    get sort() {
        return this._sort;
    }
    set sort(sort) {
        this._sort = sort;
        this.bind();
    }
    /**
     * Reconfigure the table using new key, filter, and sort. This is preferred over setting each property individually, since that results in multiple rebinds.
     * @param key The new key, or undefined if no key replacement.
     * @param filterText The new filter text, or undefined if no filter replacement. Use null for no filter.
     * @param sort The new sort model, or undefined if no sort replacement. Use null for no sort model.
     */
    reconfigure(key, filterText, sort) {
        if (!compareKey(key, this._baseKey)) {
            this._filter = filterText || undefined;
            this._sort = sort || {};
        }
        else {
            this._filter = filterText === undefined ? this.filter : filterText || undefined;
            this._sort = sort === undefined ? this.sort : sort || {};
        }
        this._baseKey = key ?? this._baseKey;
        this.bind();
        return this._baseKey;
    }
    status$ = new BehaviorSubject(undefined);
    viewName;
    indexName;
    get sortable() {
        return !!this.baseKey && this.baseKey.ex !== 'M' && this.baseKey.ex !== 'X';
    }
    /**
     * Creates filters/indexes as necessary, and then adjusts the binding of the
     * internal table client's key based on new calculated values.
     */
    async bind() {
        const baseKey = this.baseKey;
        log.debug('binding', baseKey);
        if (!baseKey) {
            this.key = undefined;
            return;
        }
        // eslint-disable-next-line prefer-const
        let [tableName, viewName, indexName] = baseKey.sym.split('#');
        const [keyTableName] = this.key?.sym.split('#') || [];
        const hasIndex = /*!!tableFilter || */ !!(this.sort && Object.keys(this.sort).length);
        const sortChanged = !isDeepEqual(this.backupSort, this.sort);
        const filterChanged = this.backupFilter !== this.filter;
        const tableChanged = tableName !== keyTableName;
        const hasFilter = !!this.filter;
        if (!this.viewName) {
            viewName = baseKey.ex === 'V' ? viewName : randomString(10);
            this.viewName = viewName;
        }
        else {
            viewName = this.viewName;
        }
        if (!this.indexName) {
            indexName = baseKey.ex === 'X' ? indexName : randomString(10);
            this.indexName = indexName;
        }
        else {
            indexName = this.indexName;
        }
        // if (viewName !== this.state.viewName || indexName !== this.state.indexName) {
        //     this.props.onStateChanged({...this.props.tableState, viewName, indexName});
        // }
        const needsView = baseKey.ex === 'V' || hasFilter;
        const createIndex = hasIndex &&
            (filterChanged ||
                sortChanged ||
                tableChanged ||
                !this.client.keys?.some((x) => x.sym === indexName && x.ex === 'X'));
        const createFilter = hasFilter && (filterChanged || !this.client.keys?.some((x) => x.sym === viewName && x.ex === 'V'));
        log.debug('root key', this.baseKey, 'hasindex', hasIndex, 'createindex', createIndex, 'hasfilter', hasFilter, 'createfilter', createFilter);
        if (createFilter || createIndex) {
            const definition = {};
            if ((createIndex || createFilter) && this.filter) {
                definition.views = [
                    {
                        filter: this.filter || '',
                        name: viewName,
                        table: tableName,
                    },
                ];
            }
            if (createIndex && this.sort && Object.keys(this.sort).length) {
                definition.indices = [
                    {
                        table: tableName,
                        name: indexName,
                        view: needsView ? viewName : undefined,
                        key_columns: Object.keys(this.sort).map((field) => ({
                            column: field,
                            sort_order: this.sort[field],
                        })),
                    },
                ];
            }
            const definitions = [definition];
            if (definitions &&
                definitions.some((x) => (!!x.indices && x.indices.length > 0) || (!!x.views && x.views.length > 0))) {
                this.status$.next({
                    error: false,
                    text: `Creating new ${[createFilter ? 'filter' : undefined, createIndex ? 'index' : undefined]
                        .filter((x) => x)
                        .join(' and ')}`,
                });
                log.info('defining objects', definitions);
                const reply$ = this.client.defineObject(false, definitions);
                const reply = await firstValueFrom(reply$);
                log.info('got object definition reply', reply);
                if (!reply.success) {
                    this.status$.next({ error: false, text: reply.comment });
                    return;
                }
                else {
                    this.status$.next(undefined);
                    // only save backups after successful creation
                    this.backupSort = this.sort;
                    this.backupFilter = this.filter;
                }
            }
        }
        const currentKey = this.key;
        log.debug('setting key based on hasFilter:', hasFilter, 'hasIndex:', hasIndex);
        if (hasFilter && hasIndex) {
            this.key = { sym: `${baseKey.sym}#${viewName}#${indexName}`, ex: 'X' };
        }
        else if (hasFilter) {
            this.key = { sym: `${baseKey.sym}#${viewName}`, ex: 'V' };
        }
        else if (hasIndex) {
            this.key = { sym: `${baseKey.sym}#${indexName}`, ex: 'X' };
        }
        else {
            /* "has nothing" */
            this.key = baseKey;
        }
        // always resubscribe when we remake, in case previous subscription generated an error
        if (!compareKey(this.key, currentKey)) {
            log.debug('set key from', currentKey, 'to', this.key);
            this.status$.next({ error: false, text: 'Subscribing to updates' });
            this.rowCount$.pipe(skip(1), first()).subscribe((x) => {
                if (x === 0) {
                    this.status$.next(undefined);
                }
            });
        }
        combineLatest([this.updates$.pipe(first()), this.descriptor$.pipe(first())]).subscribe(() => {
            this.status$.next(undefined);
        });
    }
}
//# sourceMappingURL=table.js.map